import { Editor } from '../../editor.model';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { EditorService } from '../../editor.service';
import { EditorActions } from './editor.actions';
import { tap } from 'rxjs/operators';
import { EditorEditComponent } from '../../editor-edit/editor-edit.component';
import { EditorJsonModel } from '../../editor.json.model';
import { catchError, filter, of, switchMap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';

export interface EditorStateModel {
  editors: Editor[] | undefined
}

@State<EditorStateModel>({
  name: 'editor',
  defaults: {
    editors: []
  }
})

@Injectable()
export class EditorState {
  private editorService = inject(EditorService);
  private readonly dialog = inject(MatDialog);

  @Selector()
  static getState(state: EditorStateModel) {
    return state;
  }

  @Selector()
  static getEditors(state: EditorStateModel) {
    return state.editors;
  }

  @Selector()
  static getAmountOfEditors(state: EditorStateModel) {
    return state.editors?.length;
  }

  @Action(EditorActions.Fetch)
  onFetchEditors(ctx: StateContext<EditorStateModel>) {
    return this.editorService.getMany().pipe(
      tap(res => ctx.dispatch(new EditorActions.FetchSuccess(res.data)))
    );
  }

  @Action(EditorActions.FetchSuccess)
  onFetchEditorsSuccess(ctx: StateContext<EditorStateModel>, { payload }: EditorActions.FetchSuccess) {
    ctx.setState({ editors: payload });
  }

  @Action(EditorActions.Add)
  onAdd(ctx: StateContext<EditorStateModel>, { payload }: EditorActions.Add) {
    return this.dialog.open<EditorEditComponent, EditorJsonModel, EditorJsonModel>(EditorEditComponent, { data: payload })
      .afterClosed()
      .pipe(
        filter((editor): editor is EditorJsonModel => !!editor)
      );
  }

  @Action(EditorActions.Edit)
  onEdit(ctx: StateContext<EditorStateModel>, { payload }: EditorActions.Edit) {
    return this.dialog.open<EditorEditComponent, EditorJsonModel, EditorJsonModel>(EditorEditComponent, { data: payload })
      .afterClosed()
      .pipe(
        filter((editor): editor is EditorJsonModel => !!editor),
        switchMap(editor => this.editorService.update(editor)),
        // currently ignores errors
        catchError(() => of()),
        tap(() => ctx.dispatch(new EditorActions.Fetch()))
      );
  }
}
